import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  Settings,
  Briefcase,
  Shield,
  ScrollText,
  ChevronDown,
  Building2,
  Globe,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import type { UserRole } from '@recruitment/types';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { useBatchUploadEnabled } from '@/lib/feature-flags';
import { formatRoleLabel } from '@/lib/role-labels';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import nnpcLogo from '@/assets/nnpc-logo.png';

interface NavItem {
  label: string;
  to?: string;
  icon?: React.ElementType;
  children?: NavItem[];
  roles?: UserRole[];
  activePaths?: string[];
}

function isNavItemActive(item: NavItem, pathname: string, isActive: boolean) {
  if (isActive) return true;
  return (item.activePaths ?? []).some((path) => pathname.startsWith(path));
}

const ADMIN_NAV_ITEMS: NavItem[] = [
  {
    label: 'Assessments',
    icon: ClipboardList,
    roles: ['super_admin', 'system_admin', 'hcm_admin', 'admin', 'hr'],
    children: [
      { label: 'Types & Score Criteria', to: '/admin/assessments/types' },
      { label: 'Panel Config', to: '/admin/assessments/panels' },
      { label: 'Slot Setup', to: '/admin/assessments/timetable' },
      { label: 'Batch Data Upload', to: '/admin/assessments/batch-upload' },
      { label: 'Daily Questions', to: '/admin/assessments/questions' },
      {
        label: 'Panel Member Schedule',
        to: '/admin/assessments/panel-member-schedule',
      },
      {
        label: 'Panel Role Config',
        to: '/admin/assessments/panel-role-config',
      },
    ],
  },
  {
    label: 'Job Roles',
    to: '/admin/job-roles',
    icon: Briefcase,
    roles: ['super_admin', 'system_admin', 'hcm_admin', 'admin', 'hr'],
  },
  {
    label: 'Recruitment Cycles',
    to: '/admin/cycles',
    icon: Building2,
    roles: ['super_admin', 'system_admin', 'hcm_admin', 'admin', 'hr'],
  },
  {
    label: 'Candidate Management',
    to: '/admin/users',
    icon: Users,
    roles: ['super_admin', 'system_admin', 'hcm_admin', 'admin', 'hcm_support'],
  },
  {
    label: 'Admin & Roles',
    to: '/admin/admins',
    icon: Shield,
    roles: ['super_admin', 'system_admin', 'admin'],
  },
  {
    label: 'Settings',
    to: '/admin/settings',
    icon: Settings,
    roles: ['super_admin', 'system_admin', 'admin'],
  },
];

function getInitials(name?: string | null) {
  return (name || 'User')
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function NavGroup({
  item,
  collapsed = false,
  onRequestExpand,
}: {
  item: NavItem;
  collapsed?: boolean;
  onRequestExpand?: () => void;
}) {
  const location = useLocation();
  const { hasRole } = useAuth();
  const isChildActive = item.children?.some(
    (c) =>
      (c.to && location.pathname.startsWith(c.to)) ||
      (c.activePaths ?? []).some((path) => location.pathname.startsWith(path)),
  );
  const [open, setOpen] = useState(isChildActive ?? false);

  useEffect(() => {
    if (isChildActive) setOpen(true);
  }, [isChildActive]);

  if (item.children) {
    const Icon = item.icon;
    const button = (
      <button
        onClick={() => {
          if (collapsed) {
            onRequestExpand?.();
            setOpen(true);
            return;
          }
          setOpen((p) => !p);
        }}
        type="button"
        aria-expanded={collapsed ? false : open}
        aria-label={
          collapsed ? `${item.label}. Expand sidebar to view links` : item.label
        }
        className={cn(
          'flex items-center rounded-lg text-sm font-semibold transition-colors',
          collapsed
            ? 'mx-auto h-10 w-10 justify-center p-0'
            : 'w-full gap-3 px-3 py-2',
          'text-slate-100 hover:bg-white/12 hover:text-white',
          isChildActive && 'bg-white/16 text-white shadow-sm',
        )}
      >
        {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
        {!collapsed && (
          <>
            <span className="flex-1 text-left">{item.label}</span>
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform',
                open && 'rotate-180',
              )}
              aria-hidden="true"
            />
          </>
        )}
      </button>
    );

    if (collapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <div>
        {button}
        {open && (
          <div className="ml-7 mt-1 space-y-1 border-l pl-3">
            {item.children
              .filter(
                (child) =>
                  !child.roles || child.roles.some((role) => hasRole(role)),
              )
              .map((child) => (
                <NavLink
                  key={child.to}
                  to={child.to!}
                  state={{ resetView: child.to }}
                  end={child.to === '/'}
                  className={({ isActive }) => {
                    const active = isNavItemActive(
                      child,
                      location.pathname,
                      isActive,
                    );
                    return cn(
                      'block rounded-md px-3 py-1.5 text-sm transition-colors',
                      active
                        ? 'bg-white/22 font-semibold text-white shadow-sm'
                        : 'text-slate-200 hover:bg-white/12 hover:text-white',
                    );
                  }}
                >
                  {child.label}
                </NavLink>
              ))}
          </div>
        )}
      </div>
    );
  }

  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.to!}
      state={{ resetView: item.to }}
      end={item.to === '/'}
      aria-label={collapsed ? item.label : undefined}
      className={({ isActive }) => {
        const active = isNavItemActive(item, location.pathname, isActive);
        return cn(
          'flex items-center rounded-lg text-sm font-semibold transition-colors',
          collapsed
            ? 'mx-auto h-10 w-10 justify-center p-0'
            : 'w-full gap-3 px-3 py-2',
          active
            ? 'bg-white/22 text-white shadow-sm'
            : 'text-slate-100 hover:bg-white/12 hover:text-white',
        );
      }}
    >
      {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
      {!collapsed && item.label}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

export function AppSidebar({
  mobileOpen = false,
  onMobileClose,
  desktopCollapsed = false,
  onDesktopCollapsedChange,
}: {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  desktopCollapsed?: boolean;
  onDesktopCollapsedChange?: (collapsed: boolean) => void;
}) {
  const { hasRole, hasPanelRole, user, logout } = useAuth();
  const canManageAssessments = hasRole(
    'super_admin',
    'system_admin',
    'hcm_admin',
    'admin',
    'hr',
  );
  const { isEnabled: batchUploadEnabled } = useBatchUploadEnabled({
    enabled: canManageAssessments,
  });
  const canSeePanelSessions =
    hasRole('super_admin') ||
    (hasPanelRole() && !hasRole('candidate_verifier'));
  const canSeeCandidateVerification = hasRole(
    'super_admin',
    'candidate_verifier',
  );

  const primaryNavItems: Array<NavItem & { visible?: boolean }> = [
    {
      label: 'Dashboard',
      to: '/',
      icon: LayoutDashboard,
      roles: [
        'super_admin',
        'system_admin',
        'management',
        'hcm_admin',
        'hcm_support',
      ],
    },
    {
      label: 'Applications',
      to: '/applications',
      icon: ScrollText,
      roles: ['super_admin', 'system_admin', 'management'],
    },
    {
      label: 'Panel Sessions',
      to: '/panel-sessions',
      icon: ClipboardList,
      visible: canSeePanelSessions,
      activePaths: ['/panel-sessions', '/admin/assessments/scoring'],
    },
    {
      label: 'Candidate Verification',
      to: '/candidate-verification',
      icon: Users,
      visible: canSeeCandidateVerification,
    },
    {
      label: 'Assessment Review',
      icon: Shield,
      roles: [
        'super_admin',
        'system_admin',
        'management',
        'hcm_admin',
        'hcm_reviewer',
        'qa_qc',
        'assessment_review_approver',
      ],
      children: [
        {
          label: 'QA/QC Workbench',
          to: '/admin/assessments/qa-qc',
          roles: ['super_admin', 'system_admin', 'hcm_admin', 'qa_qc'],
        },
        {
          label: 'HCM Review',
          to: '/admin/assessments/hcm-review',
          roles: ['super_admin', 'system_admin', 'hcm_admin'],
        },
        {
          label: 'Review Panels',
          to: '/admin/assessments/hcm-review-panels',
          roles: ['super_admin', 'system_admin', 'hcm_admin', 'hcm_reviewer'],
        },
        {
          label: 'Final Approval',
          to: '/admin/assessments/review-approval',
          roles: [
            'super_admin',
            'system_admin',
            'hcm_admin',
            'assessment_review_approver',
          ],
        },
        {
          label: 'HCM Review Reports',
          to: '/admin/assessments/hcm-review-reports',
          roles: ['super_admin', 'system_admin', 'management', 'hcm_admin'],
        },
      ],
    },
    {
      label: 'Reports',
      icon: Globe,
      roles: ['super_admin', 'system_admin', 'management', 'hcm_admin'],
      children: [
        {
          label: 'Assessment',
          to: '/admin/assessments/reports',
          roles: ['super_admin', 'system_admin', 'management', 'hcm_admin'],
        },
        {
          label: 'Applications',
          to: '/admin/reports/applications',
          roles: ['super_admin', 'system_admin', 'management', 'hcm_admin'],
        },
      ],
    },
  ];

  const visibleAdmin = ADMIN_NAV_ITEMS.filter(
    (item) => !item.roles || item.roles.some((r) => hasRole(r)),
  )
    .map((item) =>
      item.children
        ? {
            ...item,
            children: item.children.filter(
              (child) =>
                (batchUploadEnabled ||
                  child.to !== '/admin/assessments/batch-upload') &&
                // Children narrow their parent's roles: a group can be visible to a
                // role that may only reach some of the pages inside it.
                (!child.roles || child.roles.some((r) => hasRole(r))),
            ),
          }
        : item,
    )
    .filter((item) => !item.children || item.children.length > 0);
  const visiblePrimary = primaryNavItems.filter(
    (item) =>
      item.visible !== false &&
      (!item.roles || item.roles.some((r) => hasRole(r))),
  );

  const renderNavContent = (collapsed = false) => (
    <TooltipProvider delayDuration={150}>
      {/* Logo */}
      <div
        className={cn(
          'relative flex h-14 items-center gap-2 border-b border-sidebar-border/70 px-4 shrink-0',
          collapsed ? 'justify-center px-3' : 'justify-between',
        )}
      >
        <div
          className={cn(
            'flex min-w-0 items-center gap-2',
            collapsed && 'justify-center',
          )}
        >
          <img src={nnpcLogo} alt="NNPC Logo" className="h-9 w-auto shrink-0" />
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-none text-white">
                EMMR
              </p>
              <p className="text-xs text-white">Admin Portal</p>
            </div>
          )}
        </div>
        {onDesktopCollapsedChange && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onDesktopCollapsedChange(!collapsed)}
                className={cn(
                  'hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/75 transition-colors hover:bg-white/12 hover:text-white lg:flex',
                  collapsed &&
                    'absolute left-[3.6rem] top-3 bg-white/10 shadow-sm',
                )}
                aria-label={
                  collapsed ? 'Expand navigation' : 'Collapse navigation'
                }
                aria-pressed={collapsed}
              >
                {collapsed ? (
                  <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side={collapsed ? 'right' : 'bottom'}>
              {collapsed ? 'Expand navigation' : 'Collapse navigation'}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <ScrollArea className={cn('flex-1 py-4', collapsed ? 'px-2' : 'px-3')}>
        <nav aria-label="Primary navigation">
          <div className="space-y-1">
            {visiblePrimary.map((item) => (
              <NavGroup
                key={item.label}
                item={item}
                collapsed={collapsed}
                onRequestExpand={() => onDesktopCollapsedChange?.(false)}
              />
            ))}
          </div>

          {visibleAdmin.length > 0 && (
            <>
              <Separator className="my-4" />
              {!collapsed && (
                <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-white/70">
                  Administration
                </p>
              )}
              <div className="space-y-1">
                {visibleAdmin.map((item) => (
                  <NavGroup
                    key={item.label}
                    item={item}
                    collapsed={collapsed}
                    onRequestExpand={() => onDesktopCollapsedChange?.(false)}
                  />
                ))}
              </div>
            </>
          )}
        </nav>
      </ScrollArea>

      {/* User identity + logout */}
      <div
        className={cn(
          'shrink-0 border-t border-sidebar-border/70 py-3 space-y-1',
          collapsed ? 'px-2' : 'px-3',
        )}
      >
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex h-10 w-full items-center justify-center rounded-lg bg-white/8 text-xs font-bold text-white">
                {getInitials(user?.displayName ?? user?.staffId)}
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              {user?.displayName ?? user?.staffId ?? 'User'}
            </TooltipContent>
          </Tooltip>
        ) : (
          <div className="rounded-lg bg-white/8 px-3 py-2.5">
            <p className="truncate text-sm font-semibold text-white">
              {user?.displayName ?? user?.staffId ?? 'User'}
            </p>
            <p className="truncate text-xs text-white/55">{user?.staffId}</p>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {user?.role && (
                <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium text-white/80">
                  {formatRoleLabel(user.role)}
                </span>
              )}
            </div>
          </div>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={logout}
              aria-label="Sign out"
              className={cn(
                'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold text-red-300 transition-colors hover:bg-red-500/15 hover:text-red-200',
                collapsed && 'h-10 justify-center px-0',
              )}
            >
              <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
              {!collapsed && 'Sign Out'}
            </button>
          </TooltipTrigger>
          {collapsed && <TooltipContent side="right">Sign out</TooltipContent>}
        </Tooltip>
      </div>
    </TooltipProvider>
  );

  return (
    <>
      {/* Desktop sidebar — hidden below lg */}
      <aside
        className={cn(
          'admin-gradient hidden h-full shrink-0 flex-col border-r border-sidebar-border/70 transition-[width] duration-200 ease-out lg:flex',
          desktopCollapsed ? 'w-20' : 'w-72',
        )}
        aria-label="Main navigation"
        data-collapsed={desktopCollapsed}
      >
        {renderNavContent(desktopCollapsed)}
      </aside>

      {/* Mobile sidebar — Sheet overlay */}
      <Sheet
        open={mobileOpen}
        onOpenChange={(open) => !open && onMobileClose?.()}
      >
        <SheetContent
          side="left"
          className="admin-gradient p-0 w-64 border-r border-sidebar-border/70 [&>button]:text-white/70 [&>button]:hover:text-white"
        >
          <SheetTitle className="sr-only">Main navigation</SheetTitle>
          <SheetDescription className="sr-only">
            Mobile navigation menu for the internal recruitment portal.
          </SheetDescription>
          <div className="flex h-full flex-col">{renderNavContent(false)}</div>
        </SheetContent>
      </Sheet>
    </>
  );
}
